import { v4 as uuidv4 } from 'uuid'
import { enableMadBunday } from '~/constants/madBunday'
import { useRelishStore } from '~/store'
import type { MadBundayCode } from '~/types/madBunday'
import { madBundayTeamAfl, madBundayTeamNrl } from '~/types/madBunday'
// Global state for the Mad Bunday form on the join Relish modal

const shouldShowMadBundayPromoFromCarousel = ref<boolean>(false)
const chosenLeague = ref<MadBundayCode>('AFL')
const chosenTeam = ref<string>('')

/**
 * @description Composable for the Mad Bunday form on the join Relish modal
 */
export function useMadBundayForm() {
  function setChosenLeague(value: MadBundayCode) {
    if (chosenLeague.value !== value) {
      chosenTeam.value = ''
    }

    chosenLeague.value = value
  }

  function setChosenTeam(value: string) {
    chosenTeam.value = value
  }

  function resetMadBundayState() {
    chosenLeague.value = 'AFL'
    chosenTeam.value = ''
  }

  return {
    shouldShowMadBundayPromoFromCarousel,
    chosenLeague: readonly(chosenLeague),
    chosenTeam: readonly(chosenTeam),
    resetMadBundayState,
    setChosenLeague,
    setChosenTeam
  }
}

export function useMadBundayEntries() {
  const relishStore = useRelishStore()

  const getIsOptedOut = computed<boolean | undefined>(() => {
    return relishStore.madBundayEntries?.hasOptedOut
  })
  const getChosenTeam = computed<string | null | undefined>(() => {
    return relishStore.madBundayEntries?.team
  })
  const getLeagueText = computed<string>(() =>
    relishStore.madBundayEntries?.code === 'AFL' ? 'Your Aussie Rules Team' : 'Your Rugby League Team'
  )
  const getStats = computed(() => relishStore.madBundayEntries?.stats ?? [])

  const getChosenLeague = computed<MadBundayCode | undefined>(() => {
    if (relishStore.madBundayEntries?.code === 'AFL') {
      return relishStore.madBundayEntries?.code
    }
    if (relishStore.madBundayEntries?.code === 'NRL') {
      return relishStore.madBundayEntries?.code
    }

    return undefined
  })

  function getTeamsForLeague(league: MadBundayCode): { id: string; value: string }[] {
    return league === 'AFL'
      ? madBundayTeamAfl.map((team) => ({ id: uuidv4(), value: team }))
      : madBundayTeamNrl.map((team) => ({ id: uuidv4(), value: team }))
  }

  async function retrieveMadBundayEntries() {
    if (enableMadBunday) {
      try {
        await useRelishStore().fetchMadBundayEntries()
      } catch (error) {
        // Ignore error fetching Mad Bunday entries
      }
    }
  }

  return {
    getIsOptedOut,
    getChosenTeam,
    getChosenLeague,
    getLeagueText,
    getStats,
    getTeamsForLeague,
    retrieveMadBundayEntries
  }
}
